import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  createColumnHelper, getCoreRowModel, getSortedRowModel, getPaginationRowModel, useReactTable,
} from '@tanstack/react-table';
import { s, authHeaders, Toast, newRecordButtonStyle, getODataRows } from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { C360Table, ColumnChooser, EditIcon, DeleteIcon, unwrapLookupItems } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { parseFieldOptions, fieldOptionLabel } from './additionalFieldOptions';

const API_BASE = '/api/productrestrictions';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

// #732 Tek "Belge Tipi" alanı ikiye parçalandı: Belge Türü (kategori) + türe bağlı Belge Tipi.
// #732 Belge Türü kategorileri. Değerler KALICI kod (kayıtlı veriyi bozmamak için renumber YOK):
// 1=Alış, 2=Satış, 5=İade (#732 sonradan eklendi), 3=Depo Hareketleri, 4=Araç Yükleme Talep.
// Görüntü sırası İade'yi Satış'tan sonra gösterir; stored value 5'tir.
const DOC_CATEGORY_OPTIONS = [
  { value: '', label: 'Hepsi' },
  { value: 1, label: 'Alış' },
  { value: 2, label: 'Satış' },
  { value: 5, label: 'İade' },
  { value: 3, label: 'Depo Hareketleri' },
  { value: 4, label: 'Araç Yükleme Talep' },
];
// Belge Tipi kodları: Sipariş=1, Fatura=2, İrsaliye=3, Stok Giriş=4, Stok Çıkış=5,
// Depolar Arası Transfer=6, Araç Yükleme Talep=7. Türe göre listelenir.
const DOC_TYPE_BY_CATEGORY = {
  1: [{ value: 1, label: 'Sipariş' }, { value: 2, label: 'Fatura' }, { value: 3, label: 'İrsaliye' }],
  2: [{ value: 1, label: 'Sipariş' }, { value: 2, label: 'Fatura' }, { value: 3, label: 'İrsaliye' }],
  // #732 İade → Sipariş / İrsaliye / Fatura (Alış/Satış ile aynı belge tipleri).
  5: [{ value: 1, label: 'Sipariş' }, { value: 3, label: 'İrsaliye' }, { value: 2, label: 'Fatura' }],
  3: [{ value: 4, label: 'Stok Giriş' }, { value: 5, label: 'Stok Çıkış' }, { value: 6, label: 'Depolar Arası Transfer' }],
  4: [{ value: 7, label: 'Araç Yükleme Talep' }],
};

// Kural tipleri — backend RuleType ile birebir (0=Yasaklı, 1=Satılabilir, 2=Öncelikli).
// #740 "Öncelikli" UI'dan söküldü (yerini "Kritik SKU" menüsü aldı); backend enum korunur.
const RULE = { SELLABLE: 1, FORBIDDEN: 0, PRIORITY: 2 };
const BUCKETS = [
  {
    key: 'sellable', rule: RULE.SELLABLE, title: 'Satılabilir', short: 'Beyaz Liste',
    color: '#16a34a', soft: 'rgba(22, 163, 74, 0.10)', icon: '✓',
    hint: 'Bu liste doluyken sahada YALNIZCA bu ürünler satılabilir.',
  },
  {
    key: 'forbidden', rule: RULE.FORBIDDEN, title: 'Yasaklı', short: 'Kara Liste',
    color: '#dc2626', soft: 'rgba(220, 38, 38, 0.10)', icon: '✕',
    hint: 'Regülasyon / ticari anlaşma gereği bu ürünler satılamaz.',
  },
];

// Türkiye 7 coğrafi bölge ve illeri (Customer.Region buradan seed edilir; İl = Customer.City).
const PROVINCES_BY_REGION = {
  'Marmara': ['İstanbul', 'Edirne', 'Kırklareli', 'Tekirdağ', 'Çanakkale', 'Balıkesir', 'Bursa', 'Yalova', 'Kocaeli', 'Sakarya', 'Bilecik'],
  'Ege': ['İzmir', 'Aydın', 'Denizli', 'Muğla', 'Manisa', 'Afyonkarahisar', 'Kütahya', 'Uşak'],
  'Akdeniz': ['Antalya', 'Isparta', 'Burdur', 'Mersin', 'Adana', 'Osmaniye', 'Hatay', 'Kahramanmaraş'],
  'İç Anadolu': ['Ankara', 'Konya', 'Karaman', 'Aksaray', 'Niğde', 'Nevşehir', 'Kırşehir', 'Kırıkkale', 'Çankırı', 'Yozgat', 'Sivas', 'Eskişehir'],
  'Karadeniz': ['Zonguldak', 'Karabük', 'Bartın', 'Kastamonu', 'Sinop', 'Samsun', 'Çorum', 'Amasya', 'Tokat', 'Ordu', 'Giresun', 'Trabzon', 'Rize', 'Artvin', 'Gümüşhane', 'Bayburt', 'Bolu', 'Düzce'],
  'Doğu Anadolu': ['Erzurum', 'Erzincan', 'Kars', 'Ardahan', 'Iğdır', 'Ağrı', 'Malatya', 'Elazığ', 'Bingöl', 'Tunceli', 'Muş', 'Bitlis', 'Van', 'Hakkari'],
  'Güneydoğu Anadolu': ['Gaziantep', 'Kilis', 'Şanlıurfa', 'Adıyaman', 'Diyarbakır', 'Mardin', 'Batman', 'Siirt', 'Şırnak'],
};
const REGIONS = Object.keys(PROVINCES_BY_REGION);
const ALL_PROVINCES = Object.values(PROVINCES_BY_REGION).flat().sort((a, b) => a.localeCompare(b, 'tr'));

const emptyTargeting = () => ({
  routingGroupId: '', textId: '', description: '', documentCategory: '', documentType: '', isActive: true,
  customerGroupId: '', customerSubGroupId: '', customerId: '', region: '', province: '', district: '',
  // #734 Müşteri Özelleştirilmiş Alan kriteri + beklenen seçenek değeri.
  customerAdditionalFieldId: '', customerAdditionalFieldValue: '',
});

function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const desc = item.description || item.Description || item.name || item.Name || item.title || item.Title || '';
  return desc ? `${code} - ${desc}` : code;
}

const odataEsc = (v) => String(v).replace(/'/g, "''");

export function ProductRoutingPage() {
  usePageMeta('Ürün Rotolama', 'Satış noktalarına göre satılabilir / yasaklı / öncelikli ürün kurallarını tanımlayın.');
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [mode, setMode] = useState('list'); // 'list' | 'builder'
  const [routings, setRoutings] = useState([]);
  const [loading, setLoading] = useState(true); // ilk paint'te boş-durum yanıp sönmesin
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // routing group

  // Liste (tablo) state — diğer liste ekranlarıyla aynı desen
  const [listFilterInput, setListFilterInput] = useState('');
  const [listFilter, setListFilter] = useState('');
  const [listSelected, setListSelected] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Builder state
  const [target, setTarget] = useState(emptyTargeting());
  const [baskets, setBaskets] = useState({ sellable: [], forbidden: [] });
  // #741 Ürün havuzu (sol panel) — sunucu araması + filtreli çoklu seçim → Yasaklı/Satılabilir'e aktar.
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productSearching, setProductSearching] = useState(false);
  const [checkedProducts, setCheckedProducts] = useState(() => new Set());
  const [matchCount, setMatchCount] = useState(null);

  // Lookups
  const [customerGroups, setCustomerGroups] = useState([]);
  const [customerSubGroups, setCustomerSubGroups] = useState([]);
  const [customerLabels, setCustomerLabels] = useState({});
  // #734 Müşteri Özelleştirilmiş Alan kriteri — yalnız "Listeden Seçim" (DataType=5) tanımları + seçenekleri.
  const [customerAddFields, setCustomerAddFields] = useState([]); // [{ id, name, options: [] }]

  // Canlı önizleme — gerçek (kayıtlı) kuralı bir müşteri için test et
  const [previewCustomer, setPreviewCustomer] = useState(null);
  const [serverPreview, setServerPreview] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // --- Lookups ---
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/customergroups/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/customersubgroups/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/customeradditionalfields/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
    ]).then(([cg, csg, caf]) => {
      setCustomerGroups(unwrapLookupItems(cg));
      setCustomerSubGroups(unwrapLookupItems(csg));
      // #734/#744 Yalnız "Listeden Seçim" (DataType=5) alanları; seçenekler [{code,description}] (geriye-uyum dahil).
      setCustomerAddFields(unwrapLookupItems(caf)
        .filter((f) => Number(f.dataType ?? f.DataType) === 5)
        .map((f) => ({ id: f.id ?? f.Id, name: f.name ?? f.Name ?? f.textId ?? f.TextId ?? '', options: parseFieldOptions(f.options ?? f.Options) })));
    }).catch(() => {});
  }, []);

  const groupName = useMemo(() => {
    const map = new Map(customerGroups.map((g) => [g.id, lookupLabel(g)]));
    return (id) => (id ? map.get(id) || '' : '');
  }, [customerGroups]);
  const subGroupName = useMemo(() => {
    const map = new Map(customerSubGroups.map((g) => [g.id, lookupLabel(g)]));
    return (id) => (id ? map.get(id) || '' : '');
  }, [customerSubGroups]);

  const custFetchRef = useRef(new Set());
  const ensureCustomerLabels = useCallback(async (ids) => {
    const need = [...new Set(ids.filter((id) => id && customerLabels[id] === undefined && !custFetchRef.current.has(id)))];
    if (!need.length) return;
    need.forEach((id) => custFetchRef.current.add(id));
    try {
      for (let i = 0; i < need.length; i += 50) {
        const chunk = need.slice(i, i + 50);
        const filter = chunk.map((id) => `Id eq ${id}`).join(' or ');
        const url = `/api/customers?$top=500&$filter=${encodeURIComponent(`(${filter})`)}`;
        const res = await fetch(url, { headers: authHeaders() });
        const found = {};
        if (res.ok) { const data = await res.json(); getODataRows(data).forEach((c) => { found[c.id] = lookupLabel(c); }); }
        setCustomerLabels((prev) => { const next = { ...prev }; chunk.forEach((id) => { next[id] = found[id] || '-'; }); return next; });
      }
    } finally {
      need.forEach((id) => custFetchRef.current.delete(id));
    }
  }, [customerLabels]);
  const customerName = useCallback((id) => (id ? (customerLabels[id] || '…') : ''), [customerLabels]);

  // --- Kayıtlı rotolamaları getir (RoutingGroupId'ye göre grupla) ---
  const fetchRoutings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?$top=2000&$orderby=TextId`, { headers: authHeaders() });
      const data = res.ok ? await res.json() : {};
      const rows = getODataRows(data);
      const groups = new Map();
      for (const r of rows) {
        const key = r.routingGroupId || `legacy:${r.id}`;
        if (!groups.has(key)) {
          groups.set(key, {
            id: key,
            routingGroupId: r.routingGroupId || '', legacy: !r.routingGroupId,
            baseCode: stripSuffix(r.textId), description: r.description || '',
            documentCategory: r.documentCategory ?? '', documentType: r.documentType ?? '', isActive: false,
            customerGroupId: r.customerGroupId || '', customerSubGroupId: r.customerSubGroupId || '',
            customerId: r.customerId || '', region: r.region || '', province: r.province || '', district: r.district || '',
            types: new Set(), legacyId: r.id,
          });
        }
        const g = groups.get(key);
        g.types.add(r.ruleType ?? 0);
        if (r.isActive !== false) g.isActive = true;
      }
      const list = [...groups.values()];
      setRoutings(list);
      ensureCustomerLabels(list.map((g) => g.customerId).filter(Boolean));
    } catch (e) {
      showToast('error', 'Rotolamalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [ensureCustomerLabels]);

  useEffect(() => { fetchRoutings(); }, [fetchRoutings]);

  // --- Eşleşen nokta sayısı (canlı) ---
  const computeMatchCount = useCallback(async (t) => {
    const clauses = [];
    if (t.customerId) clauses.push(`Id eq ${t.customerId}`);
    if (t.customerGroupId) clauses.push(`GroupId eq ${t.customerGroupId}`);
    if (t.customerSubGroupId) clauses.push(`SubGroupId eq ${t.customerSubGroupId}`);
    if (t.region) clauses.push(`Region eq '${odataEsc(t.region)}'`);
    if (t.province) clauses.push(`City eq '${odataEsc(t.province)}'`);   // İl = City
    if (t.district) clauses.push(`Province eq '${odataEsc(t.district)}'`); // İlçe = Province
    const filter = clauses.join(' and ');
    let url = `/api/customers?$top=0&$count=true`;
    if (filter) url += `&$filter=${encodeURIComponent(filter)}`;
    try {
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) { setMatchCount(null); return; }
      const data = await res.json();
      const cnt = data['@odata.count'] ?? data?.Data?.['@odata.count'] ?? null;
      setMatchCount(cnt);
    } catch { setMatchCount(null); }
  }, []);

  useEffect(() => {
    if (mode !== 'builder') return;
    const id = setTimeout(() => computeMatchCount(target), 350);
    return () => clearTimeout(id);
  }, [mode, target, computeMatchCount]);

  // --- Builder aç/kapat ---
  const openNew = () => {
    setTarget(emptyTargeting());
    setBaskets({ sellable: [], forbidden: [] });
    setProductSearch(''); setProductResults([]); setCheckedProducts(new Set());
    setPreviewCustomer(null); setServerPreview(null); setMatchCount(null);
    setMode('builder');
  };

  const openEdit = async (g) => {
    setPreviewCustomer(null); setServerPreview(null);
    setProductSearch(''); setProductResults([]); setCheckedProducts(new Set());
    if (g.legacy) {
      // Eski (RoutingGroupId'siz) kayıt — GetById ile yasaklı listesini yükle, builder'a taşı.
      try {
        const res = await fetch(`${API_BASE}/GetById?Id=${g.legacyId}`, { headers: authHeaders() });
        const body = await res.json();
        const p = body?.data ?? body?.Data ?? {};
        const h = p.header || {};
        setTarget({
          routingGroupId: '', textId: stripSuffix(h.textId || ''), description: h.description || '',
          documentCategory: h.documentCategory ?? '', documentType: h.documentType ?? '', isActive: h.isActive !== false,
          customerGroupId: h.customerGroupId || '', customerSubGroupId: h.customerSubGroupId || '',
          customerId: h.customerId || '', region: h.region || '', province: h.province || '', district: h.district || '',
        });
        setBaskets({
          sellable: [],
          forbidden: (p.details || []).map((d) => ({ productId: d.productId ?? d.ProductId, label: d.label ?? d.Label ?? '' })),
        });
        if (h.customerId) ensureCustomerLabels([h.customerId]);
        setMode('builder');
      } catch { showToast('error', 'Kayıt yüklenemedi'); }
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/GetRouting?routingGroupId=${g.routingGroupId}`, { headers: authHeaders() });
      const body = await res.json();
      const p = body?.data ?? body?.Data ?? {};
      setTarget({
        routingGroupId: p.routingGroupId || '', textId: p.textId || '', description: p.description || '',
        documentCategory: p.documentCategory ?? '', documentType: p.documentType ?? '', isActive: p.isActive !== false,
        customerGroupId: p.customerGroupId || '', customerSubGroupId: p.customerSubGroupId || '',
        customerId: p.customerId || '', region: p.region || '', province: p.province || '', district: p.district || '',
        // #734 Özelleştirilmiş alan kriteri.
        customerAdditionalFieldId: p.customerAdditionalFieldId || '', customerAdditionalFieldValue: p.customerAdditionalFieldValue || '',
      });
      const map = (arr) => (arr || []).map((d) => ({ productId: d.productId ?? d.ProductId, label: d.label ?? d.Label ?? '' }));
      setBaskets({ sellable: map(p.sellable), forbidden: map(p.forbidden) });
      if (p.customerId) ensureCustomerLabels([p.customerId]);
      setMode('builder');
    } catch { showToast('error', 'Rotolama yüklenemedi'); }
  };

  const cancelBuilder = () => { setMode('list'); };

  // --- #741 Ürün havuzu (sol panel) → sepet aktarımı ---
  // Sunucu araması (debounce) — builder açıkken sol paneldeki ürün listesini besler.
  useEffect(() => {
    if (mode !== 'builder') return undefined;
    const handle = setTimeout(async () => {
      setProductSearching(true);
      try {
        const term = productSearch.trim().toLocaleLowerCase('tr').replace(/'/g, "''");
        const filter = term ? `&$filter=${encodeURIComponent(`contains(tolower(TextId),'${term}') or contains(tolower(Name),'${term}')`)}` : '';
        const res = await fetch(`/api/products?$top=50&$orderby=TextId${filter}`, { headers: authHeaders() });
        const body = await res.json().catch(() => null);
        const rows = body?.value ?? body?.Value ?? [];
        setProductResults(rows.map((p) => ({ id: p.Id ?? p.id, textId: p.TextId ?? p.textId, name: p.Name ?? p.name })));
      } catch {
        setProductResults([]);
      } finally {
        setProductSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [productSearch, mode]);

  const toggleProduct = (id) => setCheckedProducts((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Bir ürün aynı anda tek sepette olabilir → aktarırken diğer sepetten otomatik çıkar.
  const assignToBucket = (key, items) => {
    if (!items.length) return;
    const otherKey = key === 'sellable' ? 'forbidden' : 'sellable';
    setBaskets((c) => {
      const addIds = new Set(items.map((it) => it.id));
      const other = c[otherKey].filter((p) => !addIds.has(p.productId));
      const existing = new Set(c[key].map((p) => p.productId));
      const add = items
        .filter((it) => !existing.has(it.id))
        .map((it) => ({ productId: it.id, label: it.name ? `${it.textId} - ${it.name}` : it.textId }));
      return { ...c, [key]: [...c[key], ...add], [otherKey]: other };
    });
    setCheckedProducts(new Set());
  };
  const transferChecked = (key) => {
    const items = productResults.filter((p) => checkedProducts.has(p.id));
    if (items.length === 0) { showToast('error', 'Önce ürün seçin'); return; }
    assignToBucket(key, items);
  };
  const transferOne = (key, item) => assignToBucket(key, [item]);
  const removeFromBucket = (key, productId) =>
    setBaskets((c) => ({ ...c, [key]: c[key].filter((p) => p.productId !== productId) }));

  // #745 Sepetten toplu çıkar ("Havuza Gönder"): ürünler havuza döner (assignedIds'ten düşünce arama listesinde yeniden görünür).
  const removeManyFromBucket = (key, productIds) => {
    const ids = new Set(productIds);
    setBaskets((c) => ({ ...c, [key]: c[key].filter((p) => !ids.has(p.productId)) }));
  };
  // #745 Bir sepetten diğerine toplu taşı (Yasaklı ↔ Satılabilir).
  const moveBucketItems = (fromKey, toKey, productIds) => {
    const ids = new Set(productIds);
    setBaskets((c) => {
      const moving = c[fromKey].filter((p) => ids.has(p.productId));
      const from = c[fromKey].filter((p) => !ids.has(p.productId));
      const existing = new Set(c[toKey].map((p) => p.productId));
      const to = [...c[toKey], ...moving.filter((p) => !existing.has(p.productId))];
      return { ...c, [fromKey]: from, [toKey]: to };
    });
  };

  // --- Kaydet ---
  const handleSave = async () => {
    if (!target.textId.trim()) { showToast('error', 'Kod alanı zorunludur'); return; }
    const total = baskets.sellable.length + baskets.forbidden.length;
    if (total === 0) { showToast('error', 'En az bir ürün ekleyin'); return; }
    setSaving(true);
    try {
      const payload = {
        routingGroupId: target.routingGroupId || EMPTY_GUID,
        textId: target.textId.trim(), description: target.description,
        documentCategory: target.documentCategory === '' ? null : Number(target.documentCategory),
        documentType: target.documentType === '' ? null : Number(target.documentType),
        customerGroupId: target.customerGroupId || null,
        customerSubGroupId: target.customerSubGroupId || null,
        customerId: target.customerId || null,
        province: target.province || null, district: target.district || null, region: target.region || null,
        // #734 Özelleştirilmiş alan kriteri (alan seçilmediyse değer de gönderilmez).
        customerAdditionalFieldId: target.customerAdditionalFieldId || null,
        customerAdditionalFieldValue: target.customerAdditionalFieldId ? (target.customerAdditionalFieldValue || null) : null,
        isActive: !!target.isActive,
        sellableProductIds: baskets.sellable.map((p) => p.productId),
        forbiddenProductIds: baskets.forbidden.map((p) => p.productId),
        // #740 Öncelikli söküldü → her zaman boş; backend mevcut öncelikli satırları temizler.
        priorityProductIds: [],
      };
      const res = await fetch(`${API_BASE}/SaveRouting`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kaydedilemedi');
      showToast('success', 'Rotolama kaydedildi');
      setMode('list');
      fetchRoutings();
    } catch (e) {
      showToast('error', e.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  // --- Sil (gruptaki tüm tip satırları) ---
  const handleDelete = async (g) => {
    try {
      let ids = [];
      if (g.legacy) ids = [g.legacyId];
      else {
        const res = await fetch(`${API_BASE}?$top=100&$filter=${encodeURIComponent(`RoutingGroupId eq ${g.routingGroupId}`)}`, { headers: authHeaders() });
        ids = getODataRows(res.ok ? await res.json() : {}).map((r) => r.id);
      }
      for (const id of ids) {
        await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      }
      showToast('success', 'Rotolama silindi');
      setDeleteConfirm(null);
      fetchRoutings();
    } catch {
      showToast('error', 'Silme başarısız');
      setDeleteConfirm(null);
    }
  };

  // --- Canlı önizleme: seçilen test noktasının grup/il/ilçe/bölge bilgisini çek ---
  // Eşleşme ve saha listesi Builder'da target+baskets'ten ANLIK hesaplanır (kaydetmeden;
  // sepeti/hedeflemeyi değiştirdikçe canlı güncellenir). Backend Resolve'a gidilmez,
  // çünkü o yalnız kayıtlı kuralı okur.
  const runServerPreview = async (customerId) => {
    if (!customerId) { setServerPreview(null); return; }
    try {
      const url = `/api/customers?$top=1&$filter=${encodeURIComponent(`Id eq ${customerId}`)}`;
      const res = await fetch(url, { headers: authHeaders() });
      const c = getODataRows(res.ok ? await res.json() : {})[0] || {};
      setServerPreview({
        id: customerId, groupId: c.groupId, subGroupId: c.subGroupId,
        city: c.city, province: c.province, region: c.region,
      });
    } catch { showToast('error', 'Önizleme alınamadı'); }
  };

  // ===================== RENDER =====================
  const targetingSentence = (g) => {
    const parts = [];
    if (groupName(g.customerGroupId)) parts.push(`Grup: ${groupName(g.customerGroupId)}`);
    if (subGroupName(g.customerSubGroupId)) parts.push(`Ek Grup: ${subGroupName(g.customerSubGroupId)}`);
    if (g.region) parts.push(`Bölge: ${g.region}`);
    if (g.province) parts.push(`İl: ${g.province}`);
    if (g.district) parts.push(`İlçe: ${g.district}`);
    if (g.customerId) parts.push(`Nokta: ${customerName(g.customerId)}`);
    return parts.length ? parts.join(' · ') : 'Tüm noktalar';
  };

  // --- Liste tablosu (diğer liste ekranlarıyla aynı c360 deseni) ---
  const filteredRoutings = useMemo(() => {
    const q = listFilter.trim().toLocaleLowerCase('tr-TR');
    if (!q) return routings;
    return routings.filter((g) =>
      (g.baseCode || '').toLocaleLowerCase('tr-TR').includes(q) ||
      (g.description || '').toLocaleLowerCase('tr-TR').includes(q));
  }, [routings, listFilter]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('baseCode', {
      header: 'Kod',
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('description', { header: 'Açıklama', cell: (info) => info.getValue() || '-' }),
    columnHelper.display({
      id: 'targeting', header: 'Hedefleme',
      cell: ({ row }) => <span style={{ color: 'var(--text-secondary)' }}>{targetingSentence(row.original)}</span>,
    }),
    columnHelper.display({
      id: 'rules', header: 'Kurallar',
      cell: ({ row }) => (
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
          {BUCKETS.filter((b) => row.original.types?.has(b.rule)).map((b) => (
            <span key={b.key} style={{ ...typeChip, color: b.color, background: b.soft, borderColor: b.color }}>{b.icon} {b.title}</span>
          ))}
          {!BUCKETS.some((b) => row.original.types?.has(b.rule)) && '-'}
        </span>
      ),
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum',
      cell: (info) => (info.getValue() ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="bad">Pasif</StatusBadge>),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Düzenle" aria-label="Düzenle" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(row.original); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [groupName, subGroupName, customerName, canEdit, canDelete]);

  const listTable = useReactTable({
    data: filteredRoutings,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex, pageSize } },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
      setPageIndex(next.pageIndex); setPageSize(next.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const listToolbar = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod veya açıklama ile filtrele..."
        value={listFilterInput}
        onChange={(e) => setListFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { setListFilter(listFilterInput); setPageIndex(0); } }}
      />
      {listFilter && <button style={s.btnSecondary} onClick={() => { setListFilterInput(''); setListFilter(''); setPageIndex(0); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchRoutings}>Yenile</button>
      <ColumnChooser table={listTable} />
    </>
  );

  const listFooter = (
    <Pagination
      currentPage={pageIndex + 1}
      totalCount={filteredRoutings.length}
      pageSize={pageSize}
      rowCount={listTable.getRowModel().rows.length}
      onPageChange={(p) => setPageIndex(p - 1)}
      onPageSizeChange={(sz) => { setPageSize(sz); setPageIndex(0); }}
    />
  );

  return (
    <div style={s.container}>
      {mode === 'list' ? (
        <ListPageShell
          title="Ürün Rotolama"
          subtitle="Satış noktalarına göre satılabilir / yasaklı / öncelikli ürün kurallarını tanımlayın."
          actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Rotolama</button>}
          toolbar={listToolbar}
          footer={listFooter}
        >
          <C360Table
            table={listTable}
            loading={loading}
            selected={listSelected}
            onRowClick={setListSelected}
            onRowDoubleClick={openEdit}
            emptyText="Henüz rotolama tanımlanmadı. “+ Yeni Rotolama” ile başlayın."
          />
        </ListPageShell>
      ) : (
        <Builder
          target={target} setTarget={setTarget}
          baskets={baskets} removeFromBucket={removeFromBucket}
          removeManyFromBucket={removeManyFromBucket} moveBucketItems={moveBucketItems}
          productSearch={productSearch} setProductSearch={setProductSearch}
          productResults={productResults} productSearching={productSearching}
          checkedProducts={checkedProducts} toggleProduct={toggleProduct}
          transferChecked={transferChecked} transferOne={transferOne}
          customerGroups={customerGroups} customerSubGroups={customerSubGroups}
          customerAddFields={customerAddFields}
          customerName={customerName} setCustomerLabels={setCustomerLabels}
          matchCount={matchCount}
          previewCustomer={previewCustomer} setPreviewCustomer={setPreviewCustomer}
          serverPreview={serverPreview} runServerPreview={runServerPreview}
          onSave={handleSave} onCancel={cancelBuilder} saving={saving}
        />
      )}

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, lineHeight: 1.6, color: 'var(--text-primary)' }}>
                <strong>"{deleteConfirm.baseCode}"</strong> rotolamasını ve tüm kural satırlarını silmek istediğinize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={() => handleDelete(deleteConfirm)}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(null)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

// ---------- 3 bölümlü builder ----------
function Builder(props) {
  const {
    target, setTarget, baskets, removeFromBucket, removeManyFromBucket, moveBucketItems,
    productSearch, setProductSearch, productResults, productSearching,
    checkedProducts, toggleProduct, transferChecked, transferOne,
    customerGroups, customerSubGroups, customerAddFields, customerName, setCustomerLabels,
    matchCount,
    previewCustomer, setPreviewCustomer, serverPreview, runServerPreview,
    onSave, onCancel, saving,
  } = props;

  const provincesForRegion = target.region ? PROVINCES_BY_REGION[target.region] : ALL_PROVINCES;
  const totalProducts = baskets.sellable.length + baskets.forbidden.length;
  const upd = (patch) => setTarget((c) => ({ ...c, ...patch }));

  // #741 Sol paneldeki seçilebilir ürünler: aramadan gelenler, zaten bir sepette olanlar hariç.
  const assignedIds = new Set([...baskets.sellable, ...baskets.forbidden].map((p) => p.productId));
  const sourceProducts = productResults.filter((p) => !assignedIds.has(p.id));
  const dragProductRef = useRef(null);

  // #745 Sepet panelleri (Yasaklı/Satılabilir) için yerel filtre + çoklu seçim.
  const [bucketFilter, setBucketFilter] = useState({ forbidden: '', sellable: '' });
  const [bucketChecked, setBucketChecked] = useState({ forbidden: new Set(), sellable: new Set() });
  const toggleBucketChecked = (key, id) => setBucketChecked((prev) => {
    const set = new Set(prev[key]);
    if (set.has(id)) set.delete(id); else set.add(id);
    return { ...prev, [key]: set };
  });
  const clearBucketChecked = (key) => setBucketChecked((prev) => ({ ...prev, [key]: new Set() }));
  const bucketVisible = (key) => {
    const q = (bucketFilter[key] || '').trim().toLocaleLowerCase('tr');
    return baskets[key].filter((p) => !q || String(p.label || '').toLocaleLowerCase('tr').includes(q));
  };
  // "Havuza Gönder": seçili ürünleri sepetten çıkar (havuza döner).
  const sendToPool = (key) => {
    const ids = [...bucketChecked[key]];
    if (ids.length === 0) return;
    removeManyFromBucket(key, ids);
    clearBucketChecked(key);
  };
  // Diğer sepete aktar (Yasaklı↔Satılabilir).
  const moveToOther = (key) => {
    const ids = [...bucketChecked[key]];
    if (ids.length === 0) return;
    moveBucketItems(key, key === 'sellable' ? 'forbidden' : 'sellable', ids);
    clearBucketChecked(key);
  };

  // Telefon "Saha — Ürün Listesi": bir test noktası seçiliyse (serverPreview = o noktanın
  // grup/il/ilçe/bölge bilgisi) TASLAK hedeflemeyle ANLIK eşleştirilir; eşleşiyorsa taslak
  // sepetleri o noktanın saha listesi olur. Hiç nokta seçili değilse tüm taslak gösterilir.
  // Kaydetmeden çalışır; sepet/hedefleme değiştikçe canlı güncellenir.
  const live = !!serverPreview;
  const normGeo = (v) => (v == null ? '' : String(v).trim().toLocaleLowerCase('tr-TR'));
  const matched = live && (
    (!target.customerId || target.customerId === serverPreview.id) &&
    (!target.customerGroupId || target.customerGroupId === serverPreview.groupId) &&
    (!target.customerSubGroupId || target.customerSubGroupId === serverPreview.subGroupId) &&
    (!target.region || normGeo(target.region) === normGeo(serverPreview.region)) &&
    (!target.province || normGeo(target.province) === normGeo(serverPreview.city)) &&   // İl = City
    (!target.district || normGeo(target.district) === normGeo(serverPreview.province))  // İlçe = Province
  );
  const phone = !live
    ? baskets
    : matched ? baskets : { sellable: [], forbidden: [] };
  const phoneEmpty = (phone.sellable.length + phone.forbidden.length) === 0;
  const whitelistActive = matched && baskets.sellable.length > 0;

  return (
    <ListPageShell
      title="Ürün Rotolama — Builder"
      subtitle="A) Hedefleme · B) Kural Matrisi · C) Özet & Canlı Önizleme"
      actions={
        <span style={{ display: 'flex', gap: 8 }}>
          <button style={s.btnSecondary} onClick={onCancel}>← Listeye Dön</button>
          <button style={s.btnPrimary} onClick={onSave} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>
        </span>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: 16, alignItems: 'start' }}>
        {/* SOL: A + B */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {/* A) HEDEFLEME */}
          <section style={panel}>
            <SectionHead n="A" title="Hedefleme" desc="Kuralların hangi noktalara uygulanacağı" />
            <div style={s.formRow}>
              <div style={s.formGroup}>
                <label style={s.label}>Kod *</label>
                <input style={s.input} value={target.textId} onChange={(e) => upd({ textId: e.target.value })} placeholder="örn. AKARYAKIT-MARMARA" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Açıklama</label>
                <input style={s.input} value={target.description} onChange={(e) => upd({ description: e.target.value })} placeholder="Kısa açıklama" />
              </div>
              <div style={{ ...s.formGroup, maxWidth: 150 }}>
                <label style={s.label}>Belge Türü</label>
                {/* #732 Belge Türü değişince Belge Tipi sıfırlanır (türe göre seçenekler değişir). */}
                <select style={s.select} value={target.documentCategory === '' ? '' : target.documentCategory}
                  onChange={(e) => upd({ documentCategory: e.target.value === '' ? '' : Number(e.target.value), documentType: '' })}>
                  {DOC_CATEGORY_OPTIONS.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ ...s.formGroup, maxWidth: 160 }}>
                <label style={s.label}>Belge Tipi</label>
                <select style={s.select} value={target.documentType === '' ? '' : target.documentType}
                  disabled={target.documentCategory === ''}
                  onChange={(e) => upd({ documentType: e.target.value === '' ? '' : Number(e.target.value) })}>
                  <option value="">Hepsi</option>
                  {(DOC_TYPE_BY_CATEGORY[target.documentCategory] || []).map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ ...s.formGroup, maxWidth: 90 }}>
                <label style={s.label}>Aktif</label>
                <input type="checkbox" checked={!!target.isActive} onChange={(e) => upd({ isActive: e.target.checked })} style={{ width: 22, height: 22, marginTop: 6 }} />
              </div>
            </div>

            <div style={s.formRow}>
              <div style={s.formGroup}>
                <label style={s.label}>Müşteri Grubu (Segment)</label>
                <RelationPicker config={RELATION_CONFIGS.customerGroup} rows={customerGroups}
                  value={target.customerGroupId || ''}
                  selectedLabel={target.customerGroupId ? lookupLabel(customerGroups.find((g) => g.id === target.customerGroupId)) : ''}
                  onChange={(val) => upd({ customerGroupId: val })} placeholder="Tümü" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Müşteri Ek Grubu</label>
                <RelationPicker config={RELATION_CONFIGS.customerSubGroup} rows={customerSubGroups}
                  value={target.customerSubGroupId || ''}
                  selectedLabel={target.customerSubGroupId ? lookupLabel(customerSubGroups.find((g) => g.id === target.customerSubGroupId)) : ''}
                  onChange={(val) => upd({ customerSubGroupId: val })} placeholder="Tümü" />
              </div>
            </div>

            <div style={s.formRow}>
              <div style={s.formGroup}>
                <label style={s.label}>Bölge</label>
                <select style={s.select} value={target.region || ''} onChange={(e) => upd({ region: e.target.value, province: '' })}>
                  <option value="">Tümü</option>
                  {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>İl</label>
                <select style={s.select} value={target.province || ''} onChange={(e) => upd({ province: e.target.value })}>
                  <option value="">Tümü</option>
                  {provincesForRegion.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>İlçe</label>
                <input style={s.input} value={target.district || ''} onChange={(e) => upd({ district: e.target.value })} placeholder="örn. Osmangazi" />
              </div>
            </div>

            {/* #734 Müşteri Özelleştirilmiş Alan kriteri — yalnız "Listeden Seçim" alanları + seçenek bazlı değer.
                Alan tanımlı olmasa da GÖRÜNÜR (aksi halde "eklenmemiş" gibi görünüyordu); boşsa ipucu verilir. */}
            <div style={s.formRow}>
              <div style={s.formGroup}>
                <label style={s.label}>Müşteri Özelleştirilmiş Alan</label>
                <select style={s.select} value={target.customerAdditionalFieldId || ''}
                  disabled={customerAddFields.length === 0}
                  onChange={(e) => upd({ customerAdditionalFieldId: e.target.value, customerAdditionalFieldValue: '' })}>
                  <option value="">Tümü</option>
                  {customerAddFields.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
                {customerAddFields.length === 0 && (
                  <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4 }}>
                    Henüz "Listeden Seçim" türünde müşteri özelleştirilmiş alan tanımlanmamış. (Müşteri Özelleştirilmiş Alan ekranından ekleyin.)
                  </div>
                )}
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Alan Değeri (Seçenek)</label>
                <select style={s.select} value={target.customerAdditionalFieldValue || ''}
                  disabled={!target.customerAdditionalFieldId}
                  onChange={(e) => upd({ customerAdditionalFieldValue: e.target.value })}>
                  <option value="">Tümü</option>
                  {(customerAddFields.find((f) => f.id === target.customerAdditionalFieldId)?.options || []).map((o) => (
                    <option key={o.code} value={o.code}>{fieldOptionLabel(o)}</option>
                  ))}
                </select>
                {target.customerAdditionalFieldId && (
                  <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4 }}>
                    Bu kriter belge anında (kural motoru) uygulanır; aşağıdaki nokta sayısı bunu içermez.
                  </div>
                )}
              </div>
            </div>

            <div style={s.formRow}>
              <div style={{ ...s.formGroup, flex: 1 }}>
                <label style={s.label}>Tekil Nokta (Müşteri)</label>
                <RelationPicker config={RELATION_CONFIGS.customer}
                  value={target.customerId || ''}
                  selectedLabel={target.customerId ? customerName(target.customerId) : ''}
                  onChange={(val, item) => { upd({ customerId: val }); if (val && item) setCustomerLabels((prev) => ({ ...prev, [val]: lookupLabel(item) })); }}
                  placeholder="Tümü" />
              </div>
            </div>

            <div style={matchBanner}>
              <span style={{ fontWeight: 700 }}>Hedeflenen nokta sayısı:</span>{' '}
              {matchCount === null ? '…' : <span style={{ color: 'var(--accent)', fontWeight: 800 }}>{matchCount.toLocaleString('tr')}</span>}
              <span style={{ color: 'var(--text-secondary)', marginLeft: 8, fontSize: 12 }}>
                (boş kriter = “tüm noktalar”)
              </span>
            </div>
          </section>

          {/* #741 B) ÜRÜN ATAMA — 3 panel: Ürün Havuzu | Yasaklı | Satılabilir */}
          <section style={panel}>
            <SectionHead n="B" title="Ürün Atama" desc="Havuzdan ürünleri seçip Yasaklı / Satılabilir alanına aktarın (çoklu seçim, tek tık veya sürükle-bırak)" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, alignItems: 'start' }}>
              {/* Sol: ürün havuzu (arama + çoklu seçim) */}
              <div style={bucketCol}>
                <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8, color: 'var(--text-primary)' }}>Ürün Havuzu</div>
                <input style={{ ...s.input, marginBottom: 8 }} placeholder="Ürün kodu / adı ara…" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} />
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  <button style={{ ...s.btnSecondary, flex: 1, borderColor: '#dc2626', color: '#dc2626' }} onClick={() => transferChecked('forbidden')} disabled={checkedProducts.size === 0}>→ Yasaklı{checkedProducts.size ? ` (${checkedProducts.size})` : ''}</button>
                  <button style={{ ...s.btnSecondary, flex: 1, borderColor: '#16a34a', color: '#16a34a' }} onClick={() => transferChecked('sellable')} disabled={checkedProducts.size === 0}>→ Satılabilir{checkedProducts.size ? ` (${checkedProducts.size})` : ''}</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 320, overflowY: 'auto' }}>
                  {productSearching ? (
                    <span style={srcHint}>Aranıyor…</span>
                  ) : sourceProducts.length === 0 ? (
                    <span style={srcHint}>{productSearch.trim() ? 'Sonuç yok.' : 'Aramak için yazın.'}</span>
                  ) : sourceProducts.map((p) => (
                    <label key={p.id} style={srcItem} draggable onDragStart={() => { dragProductRef.current = p; }}>
                      <input type="checkbox" checked={checkedProducts.has(p.id)} onChange={() => toggleProduct(p.id)} />
                      <span style={{ fontWeight: 700, fontFamily: 'ui-monospace, monospace', flexShrink: 0 }}>{p.textId}</span>
                      <span style={{ color: 'var(--text-secondary)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* #745 Orta + Sağ: sepetler — filtre + çoklu seçim + çift yönlü aktar + sürükle-bırak hedefi */}
              {BUCKETS.map((b) => {
                const visible = bucketVisible(b.key);
                const checkedCount = bucketChecked[b.key].size;
                const otherTitle = b.key === 'sellable' ? 'Yasaklı' : 'Satılabilir';
                return (
                  <div
                    key={b.key}
                    style={{ ...bucketCol, borderTop: `3px solid ${b.color}`, minHeight: 340 }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); const it = dragProductRef.current; dragProductRef.current = null; if (it) transferOne(b.key, it); }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <span style={{ ...bucketBadge, color: b.color, background: b.soft }}>{b.icon}</span>
                      <div>
                        <div style={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: 14 }}>{b.title}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{b.short} · {baskets[b.key].length} ürün</div>
                      </div>
                    </div>
                    {baskets[b.key].length > 0 && (
                      <input
                        style={{ ...s.input, marginBottom: 6, fontSize: 12.5 }}
                        placeholder="Bu listede filtrele…"
                        value={bucketFilter[b.key]}
                        onChange={(e) => setBucketFilter((prev) => ({ ...prev, [b.key]: e.target.value }))}
                      />
                    )}
                    {checkedCount > 0 && (
                      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                        <button style={{ ...s.btnSecondary, flex: 1, fontSize: 12 }} onClick={() => sendToPool(b.key)}>Havuza Gönder ({checkedCount})</button>
                        <button style={{ ...s.btnSecondary, flex: 1, fontSize: 12 }} onClick={() => moveToOther(b.key)}>→ {otherTitle}</button>
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 300, overflowY: 'auto' }}>
                      {baskets[b.key].length === 0 ? (
                        <span style={{ ...srcHint, textAlign: 'center', padding: '20px 8px' }}>Havuzdan seçip “→ {b.title}” deyin ya da buraya sürükleyin.</span>
                      ) : visible.length === 0 ? (
                        <span style={{ ...srcHint, textAlign: 'center', padding: '12px 8px' }}>Filtreye uyan ürün yok.</span>
                      ) : visible.map((p) => (
                        <label key={p.productId} style={{ ...chipRow, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={bucketChecked[b.key].has(p.productId)}
                            onChange={() => toggleBucketChecked(b.key, p.productId)}
                            style={{ flexShrink: 0 }}
                          />
                          <span style={{ fontSize: 12, color: 'var(--text-primary)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label || '(ürün)'}</span>
                          <button style={chipX} onClick={(e) => { e.preventDefault(); removeFromBucket(b.key, p.productId); }} title="Çıkar">×</button>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {/* SAĞ: C) ÖZET & CANLI ÖNİZLEME */}
        <aside style={{ ...panel, position: 'sticky', top: 8 }}>
          <SectionHead n="C" title="Özet & Canlı Önizleme" desc="Saha temsilcisine yansıma" />

          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {BUCKETS.map((b) => (
              <div key={b.key} style={{ flex: 1, textAlign: 'center', padding: '10px 4px', borderRadius: 12, background: b.soft }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: b.color }}>{baskets[b.key].length}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>{b.title}</div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
            <strong>{totalProducts}</strong> ürün · <strong>{matchCount === null ? '…' : matchCount.toLocaleString('tr')}</strong> nokta
          </div>

          {/* Bir noktada test et — TASLAK kuralı (kaydetmeden) o noktada simüle eder */}
          <div style={{ marginBottom: 8 }}>
            <label style={{ ...s.label, display: 'block', marginBottom: 6 }}>Bir noktada test et (taslak, canlı)</label>
            <RelationPicker config={RELATION_CONFIGS.customer}
              value={previewCustomer?.value || ''}
              selectedLabel={previewCustomer?.label}
              onChange={(val, item) => { setPreviewCustomer(val ? { value: val, label: item ? lookupLabel(item) : '' } : null); runServerPreview(val); }}
              placeholder="Tüm taslağı göster" />
            {live && (
              <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: matched ? '#16a34a' : '#dc2626' }}>
                {matched ? '✓ Bu nokta hedeflemeye UYUYOR' : '✕ Bu nokta hedeflemeye uymuyor — sahada kural görmez'}
              </div>
            )}
          </div>

          {/* Telefon mockup — taslak (veya seçili nokta için çözülmüş taslak) */}
          <div style={phoneFrame}>
            <div style={phoneNotch} />
            <div style={phoneHeader}>
              Saha — Ürün Listesi
              <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.92, marginTop: 2 }}>
                {live ? `● ${previewCustomer?.label || 'Seçili nokta'}` : 'Taslak önizleme'}
              </div>
            </div>
            {whitelistActive && (
              <div style={{ fontSize: 10, fontWeight: 700, color: '#15803d', background: 'rgba(22,163,74,0.12)', padding: '4px 8px', textAlign: 'center' }}>
                Beyaz liste aktif — sahada yalnız satılabilirler görünür
              </div>
            )}
            <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
              {phoneEmpty ? (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', padding: 16 }}>
                  {live ? 'Bu nokta için gösterilecek ürün yok.' : 'Ürün ekledikçe burada görünecek'}
                </div>
              ) : (
                <>
                  {phone.sellable.map((p) => <PhoneRow key={`se-${p.productId}`} p={p} bucket={BUCKETS[0]} />)}
                  {phone.forbidden.map((p) => <PhoneRow key={`fo-${p.productId}`} p={p} bucket={BUCKETS[1]} forbidden />)}
                </>
              )}
            </div>
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '6px 2px 0', lineHeight: 1.4 }}>
            ✓ satılabilir normal, ✕ yasaklı kırmızı/üzeri çizili.
            {live
              ? ' Seçili nokta için taslak kuralın çözülmüş hali — kaydetmeden.'
              : ' Eklediğiniz taslağı anında gösterir; bir nokta seçince o noktada simüle edilir.'}
          </p>
        </aside>
      </div>
    </ListPageShell>
  );
}

function PhoneRow({ p, bucket, forbidden }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderRadius: 8,
      background: forbidden ? 'rgba(220,38,38,0.06)' : bucket.soft,
      border: `1px solid ${forbidden ? 'rgba(220,38,38,0.25)' : 'transparent'}`,
      opacity: forbidden ? 0.7 : 1,
    }}>
      <span style={{ color: bucket.color, fontWeight: 800, fontSize: 12 }}>{bucket.icon}</span>
      <span style={{
        fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
        textDecoration: forbidden ? 'line-through' : 'none',
      }}>{p.label || '(ürün)'}</span>
    </div>
  );
}

function SectionHead({ n, title, desc }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      <span style={sectionNum}>{n}</span>
      <div>
        <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-primary)' }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{desc}</div>
      </div>
    </div>
  );
}

function stripSuffix(code) {
  if (!code) return '';
  for (const sfx of ['-W', '-B', '-P']) if (code.endsWith(sfx)) return code.slice(0, -sfx.length);
  return code;
}

// ---------- styles ----------
const typeChip = {
  fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999, border: '1px solid',
};
const panel = {
  padding: 18, border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 'var(--radius, 18px)',
  background: 'var(--bg-card, #fff)', boxShadow: 'var(--shadow-card, 0 6px 16px rgba(15,23,42,0.06))',
};
const sectionNum = {
  width: 30, height: 30, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--accent-soft, rgba(0, 115, 234,0.12))', color: 'var(--accent, #0073ea)', fontWeight: 800,
};
const matchBanner = {
  marginTop: 6, padding: '10px 14px', borderRadius: 12,
  background: 'var(--accent-soft, rgba(0, 115, 234,0.10))', fontSize: 13, color: 'var(--text-primary)',
};
const bucketCol = {
  padding: 12, borderRadius: 12, background: 'var(--surface-card, #fbfcfe)',
  border: '1px solid var(--border-color, #e5e7eb)',
};
// #741 Ürün havuzu (sol panel) liste öğeleri + boş/ipucu metni.
const srcItem = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, cursor: 'pointer',
  fontSize: 12, background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #eef1f5)',
};
const srcHint = { fontSize: 12, color: 'var(--text-secondary)', padding: '8px 2px' };
const bucketBadge = {
  width: 28, height: 28, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800,
};
const chipRow = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
  padding: '6px 8px', borderRadius: 8, background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
};
const chipX = {
  border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: 16, fontWeight: 800, lineHeight: 1, padding: '0 2px',
};
const phoneFrame = {
  borderRadius: 22, border: '6px solid #0b1437', background: '#f5f7fb', overflow: 'hidden', marginTop: 6, position: 'relative',
};
const phoneNotch = {
  position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)', width: 60, height: 5, borderRadius: 4, background: 'rgba(255,255,255,0.5)', zIndex: 2,
};
const phoneHeader = {
  background: 'linear-gradient(135deg, #0450b8, #12c9ee)', color: '#fff', fontWeight: 700, fontSize: 12, padding: '14px 12px 8px', textAlign: 'center',
};
